/**
 * Auth state management
 */
import { onAuthStateChanged, onIdTokenChanged, signOut as firebaseSignOut, deleteUser as firebaseDeleteUser, } from 'firebase/auth';
import { getFirebaseAuth } from './firebase.js';
/**
 * Convert Firebase User to AuthUser
 */
function toAuthUser(user) {
    return {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        providerId: user.providerData[0]?.providerId ?? 'unknown',
    };
}
/**
 * Get current user
 */
export function getCurrentUser() {
    const auth = getFirebaseAuth();
    const user = auth.currentUser;
    return user ? toAuthUser(user) : null;
}
/**
 * Subscribe to auth state changes
 */
export function onAuthStateChange(callback) {
    const auth = getFirebaseAuth();
    return onAuthStateChanged(auth, (user) => {
        callback(user ? toAuthUser(user) : null);
    });
}
/**
 * Fires on sign-in/out AND on Firebase's own silent token refresh (roughly
 * hourly, as long as the tab stays open) - a superset of onAuthStateChange's
 * trigger conditions. Callers use this specifically to keep a cached ID
 * token (e.g. an API client's Authorization header) current; it's too
 * frequent/noisy to drive a full app re-sync from (see onAuthStateChange
 * for that). Without this, any cached token silently goes stale ~1 hour
 * into a session with the tab left open, and every authenticated request
 * fails with a 401 despite the user still being genuinely signed in -
 * confirmed as the cause of "Manage Account asks me to sign in again" 2026-09.
 */
export function onIdTokenChange(callback) {
    const auth = getFirebaseAuth();
    return onIdTokenChanged(auth, (user) => {
        callback(user ? toAuthUser(user) : null);
    });
}
/**
 * Get ID token for API calls
 */
export async function getIdToken(forceRefresh = false) {
    const auth = getFirebaseAuth();
    const user = auth.currentUser;
    if (!user) {
        return null;
    }
    return user.getIdToken(forceRefresh);
}
/**
 * Sign out
 */
export async function signOut() {
    const auth = getFirebaseAuth();
    await firebaseSignOut(auth);
}
/**
 * Delete the currently signed-in Firebase Auth account.
 *
 * The caller is responsible for clearing any backend state (e.g. D1 rows)
 * BEFORE invoking this — once the auth account is gone, the ID token can no
 * longer be issued and authenticated requests will fail.
 *
 * Throws Firebase errors as-is. The most common is `auth/requires-recent-login`
 * (the user signed in too long ago); the caller should catch that and prompt
 * a fresh sign-in before retrying.
 */
export async function deleteCurrentUser() {
    const auth = getFirebaseAuth();
    const user = auth.currentUser;
    if (!user)
        return;
    await firebaseDeleteUser(user);
}
/**
 * Check if user is authenticated
 */
export function isAuthenticated() {
    const auth = getFirebaseAuth();
    return auth.currentUser !== null;
}
/**
 * Wait for auth to be ready
 */
export function waitForAuth() {
    return new Promise((resolve) => {
        const auth = getFirebaseAuth();
        // If already have user, resolve immediately
        if (auth.currentUser !== undefined) {
            resolve(auth.currentUser ? toAuthUser(auth.currentUser) : null);
            return;
        }
        // Otherwise wait for first auth state change
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            unsubscribe();
            resolve(user ? toAuthUser(user) : null);
        });
    });
}
//# sourceMappingURL=auth.js.map